import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getExerciseNodes, type ExerciseNode, type ExerciseNodeWithoutDescription } from '../../../api/exercise-node';
import type { Node } from '../../../api/table-of-content';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { getDescriptionOfNode } from '../../../utils/getNodeDescription';
import TableOfContentsContext from '../toc/_contexts/TableOfContentsContext';
import ModuleContext from './ModuleContext';

interface ExerciseModuleContextValue {
  isLoading: boolean;
  exerciseNodes: ExerciseNode[];
  fetchExerciseNodes: () => Promise<void>;
}

export const ExerciseModuleContext = createContext<ExerciseModuleContextValue>({
  isLoading: true,
  exerciseNodes: [],
  fetchExerciseNodes: () => Promise.resolve(),
});

export function ExerciseModuleContextProvider({ children }: PropsWithChildren) {
  const handleError = useThrowAsyncError();
  const { selectedModule } = useContext(ModuleContext);
  const { nodes } = useContext(TableOfContentsContext);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [exerciseNodes, setExerciseNodes] = useState<ExerciseNode[]>([]);

  const fetchExerciseNodes = useCallback(async () => {
    if (!selectedModule?.isExerciseModule || nodes.length === 0) return;
    setIsLoading(true);

    function populateNodes(exerciseNodes: ExerciseNodeWithoutDescription[], tocNodes: Node[]): ExerciseNode[] {
      return exerciseNodes.map(exerciseNode => {
        const tocNode = tocNodes.find(n => n.id === exerciseNode.id);
        const description = getDescriptionOfNode(tocNode!, t);

        let sublevelExerciseNodes: ExerciseNode[] = [];
        if (exerciseNode.nodes?.length && tocNode?.nodes?.length) {
          sublevelExerciseNodes = populateNodes(exerciseNode.nodes, tocNode.nodes);
        }

        return {
          ...exerciseNode,
          description,
          nodes: sublevelExerciseNodes,
        } as ExerciseNode;
      });
    }

    try {
      const { data } = await getExerciseNodes(selectedModule.id);

      setExerciseNodes(populateNodes(data, nodes));
    } catch (e) {
      handleError(e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedModule, nodes, t, handleError]);

  useEffect(() => {
    fetchExerciseNodes();
  }, [fetchExerciseNodes]);

  return (
    <ExerciseModuleContext.Provider value={{ isLoading, exerciseNodes, fetchExerciseNodes }}>
      {children}
    </ExerciseModuleContext.Provider>
  );
}

export function useExerciseModuleContext() {
  return useContext(ExerciseModuleContext);
}
